import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Eye, ShieldCheck } from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Avatar } from '../../components/Avatar'
import { Modal } from '../../components/ui/Modal'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { api, extractApiError } from '../../lib/api'
import {
  auditLogsResponse,
  platformTenantsResponse,
} from '../../lib/types'
import { formatDate, formatDateTime, formatNumber } from '../../lib/format'
import { FilterRow } from '../../components/filters/FilterRow'
import {
  moduleOptions,
  actionOptions,
  actionLabel,
  actionToneClasses,
} from '../../lib/audit-actions'
import { usePageSize } from '../../lib/display-prefs'

function fmtRelative(iso) {
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  // > 7 days falls back to the platform-wide DD/MM/YYYY format so
  // the audit log + every other page render the same absolute date.
  return formatDate(iso)
}

/** Convert a `YYYY-MM-DD` input value to the ISO datetime the
 *  backend expects. `bound = 'start'` returns 00:00:00.000Z;
 *  `bound = 'end'` returns 23:59:59.999Z so the user's pick of a
 *  date is inclusive of the whole day. */
function dayToIso(day, bound) {
  if (!day) return undefined
  const time = bound === 'end' ? 'T23:59:59.999Z' : 'T00:00:00.000Z'
  return `${day}${time}`
}

/** Inverse of dayToIso — used to seed the date inputs from URL
 *  state. Ignores the time component. */
function isoToDay(iso) {
  if (!iso) return ''
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
  return m ? m[1] : ''
}

/** Today's date as YYYY-MM-DD (local) — caps the From/To pickers so a
 *  future date can't be selected. Audit events are never in the future,
 *  so a future range would only ever return zero rows. */
function todayDay() {
  const d = new Date()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mo}-${da}`
}

export function AuditLogsPage() {
  // Filters live in local React state — selecting a filter does NOT
  // mutate the URL, so the page reads cleanly as `/super-admin/audit-
  // logs` and the admin can filter freely without the address bar
  // churning. Initial values are seeded ONCE from the URL (so an older
  // bookmarked `?actionCategory=…&tenantId=…` link still lands
  // pre-filtered); after mount the URL is never written back.
  const [params] = useSearchParams()

  const [search, setSearch] = useState(() => params.get('search') ?? '')
  // `actionCategory` holds the chosen Module prefix (`tenant`, `plan`,
  // …); `action` holds a specific dot-namespaced key (`tenant.updated`)
  // once an Action is picked under that module.
  const [actionCategory, setActionCategory] = useState(
    () => params.get('actionCategory') ?? '',
  )
  const [action, setAction] = useState(() => params.get('action') ?? '')
  const [tenantId, setTenantId] = useState(() => params.get('tenantId') ?? '')
  // Date filters held as `YYYY-MM-DD` day strings; converted to ISO
  // bounds only when the query is built below.
  const [fromDay, setFromDay] = useState(() => isoToDay(params.get('from')))
  const [toDay, setToDay] = useState(() => isoToDay(params.get('to')))
  const [openId, setOpenId] = useState(() => params.get('open') ?? null)
  const [page, setPage] = useState(() =>
    Math.max(Number(params.get('page')) || 1, 1),
  )
  // A `?limit=` link wins; otherwise the list starts at (and follows) the
  // account rows-per-page (My Preferences). The API accepts up to 500.
  const [limit, setLimit] = usePageSize({ initial: Number(params.get('limit')) || null })

  // A filter change resets paging (page 5 of the old result set is
  // meaningless once the filter narrows it) and closes any open detail
  // anchor (the row it pointed at may have just filtered away).
  function afterFilterChange() {
    setPage(1)
    setOpenId(null)
  }

  // Module dropdown handler — selecting (or clearing) a Module wipes
  // any specific Action picked under the previous one, since that key
  // is meaningless against a different module.
  function setModule(value) {
    setActionCategory(value || '')
    setAction('')
    afterFilterChange()
  }

  function clearAllFilters() {
    setSearch('')
    setActionCategory('')
    setAction('')
    setTenantId('')
    setFromDay('')
    setToDay('')
    setPage(1)
    setOpenId(null)
  }

  // Pagination handlers — a page/size change closes the open detail
  // anchor; a size change also restarts at page 1.
  function changePage(next) {
    setPage(Math.max(Number(next) || 1, 1))
    setOpenId(null)
  }

  function changeLimit(next) {
    setLimit(Number(next) || null)
    setPage(1)
    setOpenId(null)
  }

  const anyFilterActive = Boolean(
    search || actionCategory || action || tenantId || fromDay || toDay,
  )
  // Cap both date pickers at today — future dates are disabled in the
  // native picker and could never match an audit event.
  const today = todayDay()

  // Shared server params for the active Search + Module→Action + Company +
  // date-range filters — used by both the paged query and the export fetch.
  function filterParams() {
    const q = {}
    if (search.trim()) q.search = search.trim()
    // A specific Action (exact key) wins over the Module prefix — the
    // backend uses an exact match when `action` is present and falls
    // back to a prefix match on `actionCategory` otherwise.
    if (action) q.action = action
    else if (actionCategory) q.actionCategory = actionCategory
    if (tenantId) q.tenantId = tenantId
    const fromIso = dayToIso(fromDay, 'start')
    const toIso = dayToIso(toDay, 'end')
    if (fromIso) q.from = fromIso
    if (toIso) q.to = toIso
    return q
  }

  const logsQuery = useQuery({
    queryKey: [
      'platform-audit-logs',
      search,
      actionCategory,
      action,
      tenantId,
      fromDay,
      toDay,
      page,
      limit,
    ],
    queryFn: async () => {
      const res = await api.get('/super-admin/audit-logs', {
        params: { ...filterParams(), page, limit },
      })
      return auditLogsResponse.parse(res.data)
    },
    // Keep the current page visible while the next one loads.
    placeholderData: (prev) => prev,
  })

  // Export pages through the filtered feed (bounded); DataTable sorts the
  // current page internally via each column's `sortAccessor`.
  async function fetchAllForExport() {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/super-admin/audit-logs', {
        params: { ...filterParams(), page: p, limit: 500 },
      })
      const parsed = auditLogsResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    return all
  }

  // Company dropdown — fetched once, reused across renders. staleTime
  // is generous because the tenant list doesn't shift often during a
  // single investigation session.
  const tenantsQuery = useQuery({
    queryKey: ['platform-tenants-dropdown'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/tenants')
      return platformTenantsResponse.parse(res.data)
    },
  })

  const tenantOptions = useMemo(() => {
    const rows = tenantsQuery.data?.data ?? []
    return [...rows]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({ value: t.id, label: t.name }))
  }, [tenantsQuery.data])

  const rawRows = logsQuery.data?.data ?? []
  const pageInfo = logsQuery.data?.pagination ?? null

  // Rows come back already sorted by the server (see the sort state
  // above), so render them as-is — a client-side re-sort would only
  // reorder the current page.
  const rows = rawRows

  // Resolve the row currently anchored by `?open=<id>` so we can pass
  // it into the detail modal. Falls back to `null` while the page is
  // still loading or when the URL points at a row not in the current
  // result set (e.g. filters changed but the open-anchor stuck around).
  const openRow = useMemo(
    () => (openId ? rows.find((r) => r.id === openId) ?? null : null),
    [rows, openId],
  )

  const columns = useMemo(
    () => [
      {
        key: 'createdAt',
        header: 'Date and Time',
        minWidth: 160,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) => (r.createdAt ? formatDateTime(r.createdAt) : ''),
        cellClassName:
          'text-sm text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (r) => (
          <span title={fmtRelative(r.createdAt)}>
            {formatDateTime(r.createdAt)}
          </span>
        ),
      },
      {
        key: 'action',
        header: 'Event',
        minWidth: 180,
        sortAccessor: (r) => actionLabel(r.action).toLowerCase(),
        exportValue: (r) => actionLabel(r.action),
        render: (r) => (
          <span
            className={
              'inline-flex max-w-full items-center px-2 py-0.5 rounded-full text-xs font-medium border truncate ' +
              actionToneClasses(r.action)
            }
            title={r.action}
          >
            {actionLabel(r.action)}
          </span>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        grow: true,
        minWidth: 240,
        sortable: false,
        exportValue: (r) => rowSummary(r),
        render: (r) => (
          <span
            className="text-sm text-[color:var(--color-ink)] leading-relaxed line-clamp-2"
            title={rowSummary(r)}
          >
            {rowSummary(r)}
          </span>
        ),
      },
      {
        key: 'user',
        header: 'User',
        minWidth: 200,
        sortAccessor: (r) => (r.actorName ?? '').toLowerCase(),
        exportValue: (r) =>
          r.actorName
            ? r.actorEmail
              ? `${r.actorName} (${r.actorEmail})`
              : r.actorName
            : '',
        render: (r) =>
          r.actorId ? (
            <span className="flex items-center gap-2.5 min-w-0">
              <Avatar name={r.actorName ?? ''} size="sm" />
              <span className="min-w-0">
                <span className="block text-sm text-[color:var(--color-ink-strong)] truncate">
                  {r.actorName ?? '—'}
                </span>
                {r.actorEmail ? (
                  <span className="block text-xs text-[color:var(--color-ink-muted)] truncate">
                    {r.actorEmail}
                  </span>
                ) : r.actorRole ? (
                  <span className="block text-xs text-[color:var(--color-ink-muted)] truncate">
                    {r.actorRole}
                  </span>
                ) : null}
              </span>
            </span>
          ) : (
            <span className="text-sm text-[color:var(--color-ink-muted)]">—</span>
          ),
      },
      {
        key: 'company',
        header: 'Company',
        minWidth: 160,
        sortAccessor: (r) => (resolveTenantName(r) ?? '').toLowerCase(),
        exportValue: (r) => resolveTenantName(r) ?? 'Platform',
        cellClassName: 'text-sm text-[color:var(--color-ink-muted)]',
        render: (r) => (
          <span className="truncate block">
            {resolveTenantName(r) ?? 'Platform'}
          </span>
        ),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action. Restore the
        // plural if a second is ever added — the columns that already carry
        // two or more use "Actions". (The audit EVENT column above is now
        // headed "Event", so there is no longer a name clash here.)
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            items={[
              {
                key: 'view',
                label: 'View details',
                icon: Eye,
                onClick: () => setOpenId(r.id),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb current="Audit Logs" homePath="/super-admin/dashboard" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <ShieldCheck size={20} className="text-[color:var(--color-accent-strong)]" />
          Audit Logs
        </h1>
        <p className="mt-1 text-[color:var(--color-ink-muted)] text-sm">
          See what changed and who did it.
        </p>
      </div>

      {/* Filters — uses the shared FilterRow primitive so every list
       *  page across the platform renders the same search + dropdowns
       *  + date pickers + refresh / clear chrome. Per the SaaS QA
       *  rules: no border around the row, search has a fixed width,
       *  dropdowns auto-size to their visible label. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={(v) => {
            setSearch(v)
            afterFilterChange()
          }}
          placeholder="Search Event"
        />
        {/* Cascading Module → Action filter. The Action dropdown stays
         *  disabled (showing "Select module first") until a Module is
         *  chosen, then lists only that module's actions. */}
        <FilterRow.Select
          ariaLabel="Module"
          placeholder="All modules"
          value={actionCategory}
          onChange={setModule}
          options={moduleOptions()}
        />
        <FilterRow.Select
          ariaLabel="Event"
          placeholder={actionCategory ? 'All events' : 'Select module first'}
          value={action}
          onChange={(v) => {
            setAction(v)
            afterFilterChange()
          }}
          options={actionOptions(actionCategory)}
          disabled={!actionCategory}
        />
        <FilterRow.Select
          ariaLabel="Company"
          placeholder="All companies"
          value={tenantId}
          onChange={(v) => {
            setTenantId(v)
            afterFilterChange()
          }}
          options={[
            { value: '', label: 'All companies' },
            ...tenantOptions,
          ]}
        />
      </FilterRow>

      <FilterRow secondary>
        <FilterRow.DateField
          label="From"
          value={fromDay}
          onChange={(v) => {
            setFromDay(v ?? '')
            afterFilterChange()
          }}
          // Stop the user picking a `from` later than the `to` (or later
          // than today) — either would always yield zero rows.
          max={toDay || today}
        />
        <FilterRow.DateField
          label="To"
          value={toDay}
          onChange={(v) => {
            setToDay(v ?? '')
            afterFilterChange()
          }}
          min={fromDay || undefined}
          max={today}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear show={anyFilterActive} onClick={clearAllFilters} />
      </FilterRow>

      {/* Server-paginated audit feed on the shared DataTable. Search +
       *  Module→Action cascade + Company + date-range filters above stay
       *  page-owned (server params); the row menu opens the detail modal. */}
      <DataTable
        tableId="platform-audit-logs"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={logsQuery.isLoading}
        fetching={logsQuery.isFetching}
        error={
          logsQuery.isError ? extractApiError(logsQuery.error).message : null
        }
        onRetry={() => logsQuery.refetch()}
        searchable={false}
        defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={changePage}
        onLimitChange={changeLimit}
        exportBaseName="audit-logs"
        exportTitle="Audit Logs"
        getExportRows={fetchAllForExport}
        emptyState={{
          icon: ShieldCheck,
          title: anyFilterActive
            ? 'No audit entries match your filters.'
            : 'No audit entries yet.',
        }}
      />

      {/* Audit-entry detail modal. Opened by clicking a row (local
       *  state — `openId`); closing it clears that state. */}
      <AuditDetailModal
        row={openRow}
        onClose={() => setOpenId(null)}
      />
    </div>
  )
}

/** Audit-entry detail modal — redesigned for readability.
 *
 *  Grouped into four sections so an admin scanning an audit row can
 *  answer four questions at a glance:
 *    • WHO  — actor name + role
 *    • WHEN — relative time + absolute timestamp
 *    • WHERE — company / platform + IP + browser (parsed UA)
 *    • WHAT — friendly action label + resource type/id (with copy)
 *
 *  Metadata is rendered as a key-value list (not raw JSON) so the
 *  admin doesn't have to mentally parse `{ "key": "value" }`. Nested
 *  objects / arrays fall back to pretty-printed JSON inside a code
 *  block so deeply structured payloads remain inspectable.
 *
 *  The modal is `open` whenever a row is passed in; passing `null`
 *  closes it. The parent owns the open state in local React state. */
function AuditDetailModal({ row, onClose }) {
  if (!row) {
    return (
      <Modal open={false} onClose={onClose} title="" size="lg">
        {null}
      </Modal>
    )
  }
  const meta =
    row.metadata && typeof row.metadata === 'object' ? row.metadata : null
  const friendlyAction = humaniseAction(row.action)
  return (
    <Modal
      open={Boolean(row)}
      onClose={onClose}
      title={friendlyAction}
      description={row.action}
      size="lg"
      // The modal is read-only; clicking outside should dismiss
      // (no form state to lose). Matches the lightbox / info-only
      // pattern documented on the Modal component.
      dismissOnBackdrop
    >
      {/* Body — one plain-English paragraph that narrates what
       *  happened, plus a small "Technical details" disclosure for
       *  power-users who need the resource ID, IP address, browser,
       *  or raw metadata.
       *
       *  This replaces the previous fact-list layout because a
       *  non-technical reader was expected to assemble the story in
       *  their head from a column of "Label: value" rows. The
       *  narrator below does that work for them.  */}
      <AuditNarrative row={row} meta={meta} />

      {(row.resourceId || row.ipAddress || row.userAgent) ? (
        <details className="mt-4 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] overflow-hidden">
          <summary className="px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] cursor-pointer hover:bg-[color:var(--color-card-hover)] select-none">
            Technical details
          </summary>
          <div className="divide-y divide-[color:var(--color-rule)] bg-[color:var(--color-card)]">
            <Fact label="Action key" value={<span className="font-mono text-xs">{row.action}</span>} />
            {row.resourceType ? (
              <Fact label="Resource" value={<span className="font-mono text-xs">{row.resourceType}</span>} />
            ) : null}
            {row.ipAddress ? (
              <Fact label="IP address" value={<span className="font-mono text-xs">{row.ipAddress}</span>} />
            ) : null}
            {row.userAgent ? (
              <Fact label="Browser" value={parseUserAgent(row.userAgent)} />
            ) : null}
          </div>
        </details>
      ) : null}
    </Modal>
  )
}

/** Plain-English narrator. Turns an audit row into one or two
 *  sentences a non-technical reader can scan in 3 seconds.
 *
 *  Composition:
 *    1. Headline sentence — `{Actor} {verb-from-action} {target}
 *       {company-context?} {whenRelative}.`
 *    2. Detail sentence (optional) — when the metadata payload is a
 *       diff, summarises what fields changed in natural English.
 *       When it's a snapshot, describes the most useful leaves.
 *       When it's empty / opaque, omitted.
 *
 *  Examples:
 *    "TaskMagix Admin updated the company Bipto 2 days ago. The plan
 *     tier changed from Free Trial to Gold."
 *    "TaskMagix Admin updated the platform maintenance settings
 *     5 hours ago. All panels are currently live (no maintenance is
 *     scheduled or active)."
 *    "TaskMagix Admin saved an email assignment 5 hours ago." */
function AuditNarrative({ row, meta }) {
  const actor = row.actorName || 'The system'
  const when = formatRelative(row.createdAt)
  const headline = buildHeadlineSentence({
    actor,
    when,
    action: row.action,
    tenantName: resolveTenantName(row),
    resourceType: row.resourceType,
  })
  const detail = buildDetailSentence({ meta, action: row.action })

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-5 py-4 space-y-2">
      <p className="text-[15px] text-[color:var(--color-ink-strong)] leading-relaxed m-0">
        {headline}
      </p>
      {detail ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] leading-relaxed m-0">
          {detail}
        </p>
      ) : null}
      <p className="text-xs text-[color:var(--color-ink-subtle)] m-0">
        {formatDateTime(row.createdAt)}
      </p>
    </div>
  )
}

/** Resolve the company name for a row. Normal tenant-scoped events carry
 *  `tenantName` (joined from the live Tenant). A `tenant.deleted` row has
 *  no live tenant to join against — tenantId is null once the company is
 *  gone — so fall back to the name captured in the delete audit's
 *  metadata; otherwise the log couldn't name what was removed. */
function resolveTenantName(row) {
  if (row?.tenantName) return row.tenantName
  if (
    row?.action === 'tenant.deleted' &&
    row?.metadata &&
    typeof row.metadata === 'object' &&
    typeof row.metadata.name === 'string'
  ) {
    return row.metadata.name
  }
  return null
}

/** Compact description for the table's Description column. Reuses the
 *  modal narrator but drops the actor (the User column shows that) and
 *  the trailing "… ago" clause (the When column shows that), then folds
 *  in the diff/snapshot detail sentence when there is one. */
function rowSummary(row) {
  const meta =
    row.metadata && typeof row.metadata === 'object' ? row.metadata : null
  let headline = buildHeadlineSentence({
    actor: '',
    when: '',
    action: row.action,
    tenantName: resolveTenantName(row),
    resourceType: row.resourceType,
  })
  // actor='' / when='' leaves a leading space and a dangling " ." —
  // tidy both, then sentence-case the result.
  headline = capitalise(headline.replace(/\s*\.\s*$/, '').trim())
  const detail = buildDetailSentence({ meta, action: row.action })
  return detail ? `${headline}. ${detail}` : `${headline}.`
}

/** Build the headline sentence. Picks a verb from the last segment
 *  of the dotted action key (`tenant.updated` → "updated"), an
 *  object phrase from the first segment (`tenant` → "the company"),
 *  and an optional company-context clause. */
function buildHeadlineSentence({ actor, when, action, tenantName, resourceType }) {
  const parts = (action || '').split('.')
  const category = parts[0] || ''
  const last = parts[parts.length - 1] || 'changed'

  // Bulk-read doesn't fit the "verb + object" shape ("bulk read a
  // notification" reads badly), so narrate it directly. The `{ count }`
  // metadata is folded in by the detail sentence ("Count was N").
  if (category === 'notification' && last === 'bulk_read') {
    return `${actor} marked all notifications as read ${when}.`
  }

  // Verb-phrase lookup. Most cases collapse to one of these; the
  // fallback uses the raw underscore-stripped action segment so any
  // new event still produces a readable sentence.
  const verbMap = {
    created: 'created',
    updated: 'updated',
    deleted: 'deleted',
    saved: 'saved',
    sent: 'sent',
    enabled: 'enabled',
    disabled: 'disabled',
    suspended: 'suspended',
    reactivated: 'reactivated',
    invited: 'invited',
    accepted: 'accepted',
    revoked: 'revoked',
    set: 'set',
    maintenance_set: 'turned on maintenance for',
    maintenance_updated: 'updated the maintenance settings for',
    password_reset_forced: 'forced a password reset on',
  }
  const verb = verbMap[last] ?? last.replace(/_/g, ' ')

  // Object-phrase lookup. The category prefix is the safest source
  // of an object noun — it stays stable across action variants
  // (`tenant.updated`, `tenant.deleted`, etc. all produce "the
  // company"). Resource-type fallback handles ad-hoc actions.
  const objectMap = {
    tenant: 'the company',
    company: 'the company',
    platform: 'the platform settings',
    user: 'the user account',
    members: 'a member',
    workspace: 'the workspace',
    department: 'the department',
    space: 'the space',
    list: 'the list',
    task: 'the task',
    plan: 'the plan',
    coupon: 'the coupon',
    email_template: 'the email template',
    email_assignment: 'an email assignment',
    email: 'an email',
    support_ticket: 'the support ticket',
    notification: 'a notification',
    payment: 'the payment',
    invoice: 'the invoice',
    leads: 'the lead',
    lead: 'the lead',
    audit: 'the audit log',
    integration: 'the integration',
    work_role: 'the work role',
    workflow: 'the workflow',
    report: 'the report',
    sop: 'the SOP',
  }
  let object = objectMap[category] || (resourceType ? `the ${humaniseKey(resourceType).toLowerCase()}` : 'this item')

  // Build the sentence. When the event is tenant-scoped and we know
  // the tenant name, mention it instead of the generic "the company".
  let sentence = `${actor} ${verb} ${object}`
  if (tenantName && category !== 'platform') {
    if (category === 'tenant' || category === 'company') {
      // Drop the generic "the company" and use the actual name.
      sentence = `${actor} ${verb} ${tenantName}`
    } else {
      sentence += ` for ${tenantName}`
    }
  }
  sentence += ` ${when}.`
  return sentence
}

/** Build the optional detail sentence describing what changed
 *  (for diffs) or the state snapshot (for flat payloads). Returns
 *  null when there's nothing useful to say. */
function buildDetailSentence({ meta, action }) {
  if (!meta || typeof meta !== 'object' || Object.keys(meta).length === 0) {
    return null
  }
  // Special-case: maintenance snapshot. The shape is
  // `{ value: { website: {enabled, ...}, company: {...}, user: {...} } }`
  // — describe the state in plain English.
  if (action === 'platform.maintenance_updated' || action === 'platform.maintenance_set') {
    const inner = stripEnvelope(meta)
    const labels = { website: 'Website', company: 'Company Admin Panel', user: 'User Panel' }
    const down = []
    const scheduled = []
    for (const [key, label] of Object.entries(labels)) {
      const s = inner?.[key]
      if (!s || typeof s !== 'object') continue
      if (s.enabled) down.push(label)
      else if (s.scheduledStart) scheduled.push({ label, start: s.scheduledStart, end: s.scheduledEnd })
    }
    if (down.length === 0 && scheduled.length === 0) {
      return 'All panels are currently live — no maintenance is scheduled or active.'
    }
    const parts = []
    if (down.length > 0) {
      parts.push(`${joinList(down)} ${down.length === 1 ? 'is' : 'are'} currently in maintenance`)
    }
    if (scheduled.length > 0) {
      const phrases = scheduled.map(
        (s) => `${s.label} is scheduled from ${formatDateTime(s.start)}${s.end ? ` to ${formatDateTime(s.end)}` : ''}`,
      )
      parts.push(joinList(phrases))
    }
    return capitalise(parts.join('. ')) + '.'
  }

  const diff = extractDiff(meta)
  if (diff) {
    const rows = buildDiffRows(diff.previous, diff.next)
    if (rows.length === 0) return 'No fields actually changed.'
    const phrases = rows.map(({ label, from, to }) => {
      if (from === undefined) {
        return `${label.toLowerCase()} was set to ${describeValue(to)}`
      }
      if (to === undefined) {
        return `${label.toLowerCase()} was removed (was ${describeValue(from)})`
      }
      return `${label.toLowerCase()} changed from ${describeValue(from)} to ${describeValue(to)}`
    })
    return capitalise(joinList(phrases)) + '.'
  }

  // Generic flat payload. Pick a handful of leaves and stitch them
  // into a sentence. Long / nested payloads get a soft fallback
  // ("Carried N detail fields — open Technical details for the full
  // breakdown.") so we never produce a wall of text.
  const stripped = stripEnvelope(meta)
  const leaves = flattenLeaves(stripped, []).filter(
    ({ value }) =>
      value !== null && value !== undefined && value !== '' &&
      !(Array.isArray(value) && value.length === 0),
  )
  if (leaves.length === 0) return null
  if (leaves.length > 6) {
    return `Carried ${leaves.length} additional detail field${leaves.length === 1 ? '' : 's'}.`
  }
  const phrases = leaves.map(
    ({ path, value }) =>
      `${path.map(humaniseKey).join(' › ').toLowerCase()} was ${describeValue(value)}`,
  )
  return capitalise(joinList(phrases)) + '.'
}

/** Plain-English single-value describer used inside narrative
 *  sentences. Booleans → "yes" / "no", null/empty → "not set",
 *  ISO datetimes → formatted, everything else → quoted string. */
function describeValue(v) {
  if (v === null || v === undefined || v === '') return 'not set'
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (typeof v === 'number') return formatNumber(v)
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return formatDateTime(v)
    return `"${v}"`
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return 'empty'
    if (v.every((x) => typeof x !== 'object')) {
      return v.map((x) => describeValue(x)).join(', ')
    }
    return `${v.length} item${v.length === 1 ? '' : 's'}`
  }
  if (typeof v === 'object') return 'a structured value'
  return String(v)
}

/** "a, b, and c" — Oxford-comma joined for readability. */
function joinList(items) {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

function capitalise(s) {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** One row in the fact list — `Label  Value`. The label is fixed at
 *  160 px so values line up neatly down the right column. Wraps to
 *  a stacked layout on narrow viewports. */
function Fact({ label, value }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 sm:gap-4 px-4 py-2.5">
      <div className="text-xs font-medium text-[color:var(--color-ink-muted)] sm:pt-0.5">
        {label}
      </div>
      <div className="min-w-0 text-sm text-[color:var(--color-ink-strong)] break-words">
        {value}
      </div>
    </div>
  )
}

/** Strip the conventional `{ value: { … } }` / `{ data: { … } }` /
 *  `{ payload: { … } }` envelope when the wrapper is the ONLY
 *  top-level key. Otherwise leave the object as-is. */
function stripEnvelope(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj
  const keys = Object.keys(obj)
  if (keys.length === 1) {
    const k = keys[0]
    const inner = obj[k]
    if (
      (k === 'value' || k === 'data' || k === 'payload') &&
      inner &&
      typeof inner === 'object' &&
      !Array.isArray(inner)
    ) {
      return inner
    }
  }
  return obj
}

/** Walk an object and emit one entry per leaf field. Arrays + nested
 *  objects-of-objects-of-objects all collapse so the table stays
 *  flat and scannable. Returns `[{ path: [...], value }]`. */
function flattenLeaves(obj, parentPath) {
  const out = []
  if (obj === null || obj === undefined) {
    out.push({ path: parentPath, value: obj })
    return out
  }
  if (typeof obj !== 'object' || Array.isArray(obj)) {
    out.push({ path: parentPath, value: obj })
    return out
  }
  const entries = Object.entries(obj)
  if (entries.length === 0) {
    out.push({ path: parentPath, value: obj })
    return out
  }
  for (const [k, v] of entries) {
    const childPath = parentPath.concat(k)
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out.push(...flattenLeaves(v, childPath))
    } else {
      out.push({ path: childPath, value: v })
    }
  }
  return out
}

/** Identify whether the metadata is a before/after pair under any of
 *  the common naming conventions. Returns `{ previous, next }` when
 *  it is, `null` when it isn't. */
function extractDiff(meta) {
  if (!meta || typeof meta !== 'object') return null
  const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v)
  const tries = [
    ['previous', 'changes'],
    ['previous', 'next'],
    ['before', 'after'],
    ['old', 'new'],
    ['from', 'to'],
  ]
  for (const [a, b] of tries) {
    if (a in meta && b in meta && isObj(meta[a]) && isObj(meta[b])) {
      return { previous: meta[a], next: meta[b] }
    }
  }
  return null
}

/** Walk both sides of the diff and emit one row per leaf path that
 *  actually changed. Nested objects are flattened with dot paths so
 *  `features.sop_integration` reads as "Features → Sop Integration"
 *  on its own row. Arrays are compared by JSON.stringify because a
 *  per-element diff would be more noise than signal in an audit log. */
function buildDiffRows(previous, next) {
  const rows = []
  const seen = new Set()
  function walk(prev, curr, pathParts) {
    const keys = new Set([
      ...Object.keys(prev ?? {}),
      ...Object.keys(curr ?? {}),
    ])
    for (const k of keys) {
      const p = pathParts.concat(k)
      const pPath = p.join('.')
      if (seen.has(pPath)) continue
      const pv = prev?.[k]
      const cv = curr?.[k]
      const bothObj =
        pv && typeof pv === 'object' && !Array.isArray(pv) &&
        cv && typeof cv === 'object' && !Array.isArray(cv)
      if (bothObj) {
        walk(pv, cv, p)
        continue
      }
      if (sameValue(pv, cv)) continue
      seen.add(pPath)
      rows.push({
        path: pPath,
        label: p.map(humaniseKey).join(' › '),
        from: pv,
        to: cv,
      })
    }
  }
  walk(previous, next, [])
  return rows
}

function sameValue(a, b) {
  if (a === b) return true
  if (a === null || b === null) return false
  if (typeof a === 'object' && typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return false
    }
  }
  return false
}

/** "email_assignment.saved" → "Email Assignment Saved". Splits on
 *  dots and underscores, title-cases each word. Mirrors the
 *  `titleCaseKey` helper used on the Settings page so the audit copy
 *  reads the same as the platform-settings labels. */
function humaniseAction(action) {
  if (!action || typeof action !== 'string') return 'Audit Entry'
  return action
    .split(/[._]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Same humanise pass on metadata keys + role names so internal
 *  enum values ("super_admin") render as "Super Admin". */
function humaniseKey(key) {
  if (!key || typeof key !== 'string') return key
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase → camel Case
    .split(/[._\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** "32 seconds ago" / "5 minutes ago" / "Yesterday at 14:30" / etc.
 *  Less precise than `formatDateTime`, more skimmable. */
function formatRelative(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const sec = Math.round(diffMs / 1000)
  if (sec < 60) return `${sec} second${sec === 1 ? '' : 's'} ago`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`
  return formatDateTime(iso)
}

/** Cheap UA → "Browser on OS" sniff. Not perfect, but enough for an
 *  audit-row "where did this happen" glance without making the admin
 *  read a 150-char user-agent string. */
function parseUserAgent(ua) {
  if (!ua) return '—'
  const browser =
    /edg\//i.test(ua) ? 'Edge'
      : /chrome\//i.test(ua) ? 'Chrome'
      : /firefox\//i.test(ua) ? 'Firefox'
      : /safari\//i.test(ua) ? 'Safari'
      : 'Browser'
  const os =
    /windows nt/i.test(ua) ? 'Windows'
      : /mac os x/i.test(ua) ? 'macOS'
      : /android/i.test(ua) ? 'Android'
      : /iphone|ipad/i.test(ua) ? 'iOS'
      : /linux/i.test(ua) ? 'Linux'
      : ''
  return os ? `${browser} on ${os}` : browser
}
